import { MutationUpdaterFn, useApolloClient } from "@apollo/client";
import TextareaAutosize from "@material-ui/core/TextareaAutosize";
import SendIcon from "@material-ui/icons/Send";
import clsx from "clsx";
import firebase from "firebase/app";
import { Formik, useFormikContext } from "formik";
import { useTranslation } from "next-i18next";

import { DialogEnum } from "@/consts";
import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { getCreateCommentInput } from "@/features/comment/helper";
import { GetCreateCommentInputArgs } from "@/features/comment/type";
import { LoadingSpinner } from "@/features/common/components/animation/LoadingSpinner";
import { useUIContext } from "@/features/common/hooks/UIContext/useUIContext";
import { useHandleGraphqlError } from "@/features/common/hooks/useHandleGraphqlError";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import {
  CreateCommentMutation,
  useCreateCommentMutation,
  UserStatusEnum,
} from "@/generated/generated-hooks";

type CommentTextareaProps = {
  minHeight?: number;
  update: MutationUpdaterFn<CreateCommentMutation>;
  onUpdated?: () => void;
  canLeaveComment: boolean;
} & GetCreateCommentInputArgs;
export const CommentTextarea: React.FC<CommentTextareaProps> = (props) => {
  const client = useApolloClient();
  const { user } = useUserContext();
  const { openDialog } = useUIContext();
  const {
    parentCommentId,
    postId,
    update,
    onUpdated,
    canLeaveComment,
    parentName,
  } = props;
  const [createComment] = useCreateCommentMutation({
    update,
    onCompleted: () => {
      client.refetchQueries({
        include: ["commentPage"],
      });
    },
  });
  const handleGraphqlError = useHandleGraphqlError();
  const { t } = useTranslation(["common", "post"]);

  const userIsBanned = !!user && user.userStatus === UserStatusEnum.Banned;

  const disabled = userIsBanned || !canLeaveComment;

  const getPlaceholder = () => {
    if (userIsBanned)
      return t("post:comment.errors.commentAccessDeniedUserBanned");
    if (!canLeaveComment) return t("post:comment.unlockCommentAfterUnlockPost");
    if (parentCommentId)
      return t("post:comment.childCommentTextareaPlaceholder", {
        user: parentName,
      });
    return t("post:comment.commentTextareaPlaceholder");
  };

  const onTextareaFocus = (e) => {
    if (user) return;
    openDialog(DialogEnum.Login);
    e.target.blur();
  };

  return (
    <>
      <Formik
        initialValues={getCreateCommentInput({ parentCommentId, postId })}
        enableReinitialize
        onSubmit={async (values, action) => {
          try {
            action.setSubmitting(true);
            if (parentCommentId)
              firebase
                .analytics()
                .logEvent(AnalyticsEventEnum.CommentReplyToParent, {
                  parentCommentId,
                  postId,
                });

            if (!parentCommentId)
              firebase.analytics().logEvent(AnalyticsEventEnum.CommentNew, {
                postId,
              });
            await createComment({ variables: { input: { ...values } } });
            action.resetForm();
            action.setSubmitting(false);
            if (onUpdated) onUpdated();
          } catch (e) {
            action.setSubmitting(false);
            handleGraphqlError({
              e,
              t,
              sentryCaptureContext: {
                tags: {
                  mutations: "useCreateCommentMutation",
                },
              },
            });
          }
        }}
      >
        {(props) => (
          <div
            className={clsx(
              "flex border border-gray-200 shadow-sm rounded-md bg-white focus-within:border-primary p-2",
              disabled && "bg-disabled",
            )}
            data-test-id="CommentTextarea-container"
          >
            <TextareaAutosize
              className="resize-none outline-none rounded-lg border-none flex-1 text-base  text-gray-900 bg-transparent"
              onFocus={user ? null : onTextareaFocus}
              rowsMax={10}
              value={props.values.content}
              name="content"
              placeholder={getPlaceholder()}
              onChange={props.handleChange}
              disabled={props.isSubmitting || disabled}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  props.handleSubmit();
                }
              }}
            />

            {props.values.content.trim() && (
              <button
                disabled={props.isSubmitting}
                type="button"
                className="self-end"
                onClick={() => props.handleSubmit()}
              >
                {props.isSubmitting ? (
                  <LoadingSpinner svgClassName="w-5 h-5" />
                ) : (
                  <SendIcon className="text-gray-300 w-5 h-5 touchable" />
                )}
              </button>
            )}
          </div>
        )}
      </Formik>
    </>
  );
};
